import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { downloadFile } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useConnection } from '../lib/hooks.js';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { Modal } from './Modal.jsx';
import {
  IconArchive, IconBack, IconChart, IconCloudOff, IconExchange, IconHome, IconLogout, IconMore,
  IconBuy, IconSell, IconSettings, IconTank, IconTruck, IconUsers, IconWallet, IconBadge, IconRefresh,
} from './Icons.jsx';

const PRIMARY = [
  { to: '/', label: t.nav.home, icon: IconHome, end: true },
  { to: '/buy', label: t.nav.buy, icon: IconBuy },
  { to: '/sell', label: t.nav.sell, icon: IconSell },
  { to: '/customers', label: t.nav.customers, icon: IconUsers },
];

const SECONDARY = [
  { to: '/suppliers', label: t.nav.suppliers, icon: IconTruck },
  { to: '/tanks', label: t.nav.tanks, icon: IconTank },
  { to: '/expenses', label: t.nav.expenses, icon: IconWallet },
  { to: '/exchange', label: t.nav.exchange, icon: IconExchange },
  { to: '/shareholders', label: t.nav.shareholders, icon: IconBadge },
  { to: '/payroll', label: t.nav.payroll, icon: IconUsers },
  { to: '/reports', label: t.nav.reports, icon: IconChart },
  { to: '/settings', label: t.nav.settings, icon: IconSettings },
  // A backup file holds every record in the facility, including every
  // password hash - only the super admin can see or use this tab.
  { to: '/backup', label: t.nav.backup, icon: IconArchive, superAdminOnly: true },
];

const AUTO_BACKUP_KEY = 'diesel-last-auto-backup';
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Once a week, on the super admin's own device, quietly re-download a fresh backup. */
function useWeeklyAutoBackup(enabled) {
  const tried = useRef(false);
  useEffect(() => {
    if (!enabled || tried.current) return;
    tried.current = true;
    const last = Number(localStorage.getItem(AUTO_BACKUP_KEY) || 0);
    if (Date.now() - last < WEEK_MS) return;
    downloadFile('/api/backup/export', 'diesel-backup.json')
      .then(() => localStorage.setItem(AUTO_BACKUP_KEY, String(Date.now())))
      .catch(() => {
        // Silent - the manual "Download backup" button still works, and this
        // will simply retry the next time the app is opened.
      });
  }, [enabled]);
}

export function Layout() {
  const { user, settings, signOut, isAdmin, isSuperAdmin } = useAuth();
  const { online, pending, syncing, sync } = useConnection();
  const [moreOpen, setMoreOpen] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  useWeeklyAutoBackup(isSuperAdmin);

  const secondary = SECONDARY.filter(
    (item) => (!item.adminOnly || isAdmin) && (!item.superAdminOnly || isSuperAdmin),
  );

  const runSync = async () => {
    const result = await sync();
    if (result.sent) toast.success(t.connection.sent(result.sent));
    else if (result.remaining) toast.warn(t.connection.stillWaiting);
  };

  return (
    <div className="app-shell">
      <nav className="sidebar" aria-label={t.nav.main}>
        <div className="brand">
          <img src="/icons/icon-192.png" alt="" />
          <span className="truncate">{settings.facility_name || t.app.name}</span>
        </div>
        {PRIMARY.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}
        <div className="section">{t.nav.records}</div>
        {secondary.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}
        <div className="spacer" />
        <button type="button" className="btn btn-ghost" onClick={signOut}>
          <IconLogout className="flip-x" />
          {t.nav.signOut} {user?.username ? `(${user.username})` : ''}
        </button>
      </nav>

      <div className="app-main">
        {(!online || pending > 0) && (
          <div className="offline-bar">
            <IconCloudOff style={{ width: 18, height: 18 }} />
            <span>
              {!online && t.connection.offline}
              {pending > 0 && t.connection.waiting(pending)}
            </span>
            {pending > 0 && online && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={runSync} disabled={syncing}>
                {syncing ? <span className="spinner" /> : <IconRefresh />}
                {t.connection.sendNow}
              </button>
            )}
            {pending > 0 && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => navigate('/queue')}>
                {t.connection.view}
              </button>
            )}
          </div>
        )}
        <Outlet />
      </div>

      <nav className="bottom-nav" aria-label={t.nav.main}>
        {PRIMARY.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}
        <button
          type="button"
          className="bottom-nav-more"
          onClick={() => setMoreOpen(true)}
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', font: 'inherit', cursor: 'pointer' }}
        >
          <span style={{ display: 'grid', placeItems: 'center', gap: 3, height: 'var(--nav-height)', fontSize: '0.7rem', fontWeight: 600 }}>
            <IconMore style={{ width: 22, height: 22 }} />
            {t.nav.more}
          </span>
        </button>
      </nav>

      {moreOpen && (
        <Modal title={t.nav.more} onClose={() => setMoreOpen(false)}>
          <div className="record-list">
            {secondary.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} className="record row" onClick={() => setMoreOpen(false)}>
                <Icon style={{ width: 20, height: 20, color: 'var(--accent)' }} />
                <span className="strong">{label}</span>
              </NavLink>
            ))}
            <button type="button" className="record row" onClick={signOut}>
              <IconLogout className="flip-x" style={{ width: 20, height: 20, color: 'var(--danger)' }} />
              <span className="strong">{t.nav.signOut}</span>
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function NavItem({ to, label, icon: Icon, end }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => (isActive ? 'active' : '')}>
      <Icon />
      <span>{label}</span>
    </NavLink>
  );
}

/** Sticky page header. Every screen renders one as its first child. */
export function PageHeader({ title, subtitle, actions, back }) {
  const navigate = useNavigate();
  return (
    <header className="topbar">
      {back && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => navigate(back === true ? -1 : back)}
          aria-label={t.common.back}
        >
          {/* Back points along the reading direction, so it mirrors in Arabic. */}
          <IconBack className="flip-x" />
        </button>
      )}
      <div className="topbar-title">
        <h1 className="truncate">{title}</h1>
        {subtitle && <div className="sub truncate">{subtitle}</div>}
      </div>
      {actions}
    </header>
  );
}
